import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Organization} from '@models/organization/organization';
import {NotificationService} from '@services/notification/notification.service';
import {OrganizationService} from '@services/administration/organization/organization.service';
import {forkJoin} from 'rxjs';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationDialogComponent} from '@shared/confirmation-dialog/confirmation-dialog.component';
import {Machine} from '@models/machine/machine';
import {User} from '@models/user/user';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {Router} from '@angular/router';
import {OrganizationEditComponent} from '@components/administration/organization/modal/organization-edit.component';
import {ModalService} from '@services/modal/modal.service';
import {btou} from '@prototypes/global.functions';

@Component({
    selector: 'scitis-admin-organization-action',
    templateUrl: './organization-action.component.html',
    styleUrls: ['./organization-action.component.scss'],
    standalone: false
})
export class OrganizationActionComponent {
  @Input()
  organization: Organization | any;
  @Input()
  restrictOrganizationInteraction: boolean = false;
  @Input()
  isAdminRole: boolean = false;
  @Input()
  isSuperAdminRole: boolean = false;
  @Input()
  rootOrganizationId: number = 0;
  @Input()
  searchTerm: string = '';
  @Output()
  organizationDeleteEvent = new EventEmitter<Organization>();
  private COMPONENT_NAME: string = 'OrganizationActionComponent';

  constructor(private router: Router,
              private modalService: ModalService,
              private notification: NotificationService,
              private loadingIndicator: LoadingIndicatorService,
              private organizationService: OrganizationService,
              private translate: TranslateService) {
  }

  onDeleteOrganization(organization: Organization) {
    this.loadingIndicator.show(this.translate.instant('Verify if deletion possible'));
    const machinesSubscription = this.organizationService.listMachines(this.organization.id);
    const usersSubscription = this.organizationService.listUsers(this.organization.id);
    const subOrganizationsSubscription = this.organizationService.listSubOrganizations(this.organization.id);
    forkJoin([machinesSubscription, usersSubscription, subOrganizationsSubscription]).subscribe((result) => {
      const machines = result[0];
      const users = result[1];
      const subOrganizations = result[2];
      this.loadingIndicator.hide();
      if ((machines && machines.length > 0) || (users && users.length > 0) || (subOrganizations && subOrganizations.length > 0)) {
        this.showDeleteNotPossibleWarningModal(organization, machines, users, subOrganizations);
      } else {
        this.showDeleteWarningModalAndDelete(organization);
      }

    }, (error) => {
      console.error(`${this.COMPONENT_NAME}. error loading organization machines, users or sub-organizations`, error);
      this.loadingIndicator.hide();
    });

  }

  onEditOrganization(organization: Organization) {
    const organizationBackup = this.copyOrganizationProperties(organization, null);

    const modalRef = this.modalService.open(OrganizationEditComponent, {size: 'lg'});
    modalRef.componentInstance.isSuperAdminRole = this.isSuperAdminRole;
    modalRef.componentInstance.rootOrganizationId = this.rootOrganizationId;
    modalRef.componentInstance.organization = organization;
    modalRef.result.then((result) => {
      this.organization = this.copyOrganizationProperties(result, this.organization);
      this.loadingIndicator.show();
      this.organizationService.saveOrganization(result).subscribe({
        next: (result: Organization) => {
          const message = this.translate.instant('Organization') + ' ' + result.name + ' ' +
              this.translate.instant('updated') + '.';
          this.organization = {...result};
          this.loadingIndicator.hide();
          this.notification.success(message, this.COMPONENT_NAME, false);
        }, error: (error) => {
          this.loadingIndicator.hide();
          this.notification.error(this.translate.instant('Error updating organization') + ': ' + result.name,
              error, this.COMPONENT_NAME, true);
          this.organization = this.copyOrganizationProperties(organizationBackup, this.organization);
        }
      });
    }, () => {
      this.organization = this.copyOrganizationProperties(organizationBackup, this.organization);
    });
  }

  onResetUserAgreements(organization: Organization) {
    console.log(`${this.COMPONENT_NAME}. reset user agreement accepted for all users of organization ${organization.name}`);
    const resetUserAgreementsTitle = this.translate.instant('Reset user agreements');
    const resetUserAgreementsBody = this.translate.instant('Do you really want to revoke all accepted agreements of the users for the organization %s ?').replace(
        '%s', organization.name.toBold());
    const resetUserAgreementsOK = this.translate.instant('Reset');
    const resetUserAgreementsCancel = this.translate.instant('Cancel');
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = resetUserAgreementsTitle;
    modalRef.componentInstance.body = resetUserAgreementsBody;
    modalRef.componentInstance.cancelButtonText = resetUserAgreementsCancel;
    modalRef.componentInstance.okButtonText = resetUserAgreementsOK;

    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE) {
        return;
      }
      this.loadingIndicator.show('Reset user agreements');
      this.organizationService.resetUsersAgreements(organization).subscribe(() => {
        this.notification.success(this.translate.instant('successfully reset user agreement accepted for organization')
            + ' ' + organization.name + '. ', this.COMPONENT_NAME, false);
        this.loadingIndicator.hide();
      }, (error) => {
        this.notification.error(
            this.translate.instant('Reset user agreement for organization') + ' ' +
            organization.name + ' ' + this.translate.instant('failed'), error, this.COMPONENT_NAME, true);
        this.loadingIndicator.hide();
      });
    });

  }

  onRedirectToConfiguration(organization: Organization) {
    const parameters = {
      id: organization.id,
      name: organization.description,
      isSuperAdminRole: this.isSuperAdminRole,
      isAdminRole: this.isAdminRole,
      parentOrganizationId: organization.parentTenant || undefined
    };
    const encodedParams = btou(JSON.stringify(parameters));
    this.router.navigate(
        ['organizations', encodedParams],
        {state: {search: this.searchTerm}}
    );
  }

  private showDeleteNotPossibleWarningModal(organization: Organization, machines: Machine[], users: User[], subOrganizations: Organization[]) {
    const title = this.translate.instant('Delete organization');
    const body = this.createOrganizationRemoveWarningBody(organization, machines, users, subOrganizations);
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.body = body;
    modalRef.componentInstance.cancelButtonText = null;
    modalRef.componentInstance.okButtonText = this.translate.instant('Close');

    modalRef.result.then();
  }

  private showDeleteWarningModalAndDelete(organization: Organization) {
    console.log(`${this.COMPONENT_NAME}. removing organization: ${organization.name}`);

    // open modal
    const removeMessageTitle = this.translate.instant('Delete organization');
    const removeMessageBody = this.translate.instant('Do you really want to permanently delete organization %s ?').replace('%s', organization.name.toBold());
    const removeMessageOK = this.translate.instant('Delete');
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = removeMessageTitle;
    modalRef.componentInstance.body = removeMessageBody;
    modalRef.componentInstance.cancelButtonText = '';
    modalRef.componentInstance.okButtonText = removeMessageOK;

    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE) {
        return;
      }
      this.loadingIndicator.show(this.translate.instant('Deleting organization'));
      this.organizationService.delete(organization.id).subscribe((success) => {
        if (success) {
          this.notification.success(this.translate.instant('Organization') + ' ' + organization.name + ' ' +
              this.translate.instant('deleted') + '.', this.COMPONENT_NAME, false);
          this.organizationDeleteEvent.emit(organization);
          this.loadingIndicator.hide();
        } else {
          this.notification.error(
              this.translate.instant('Organization could not be deleted') + ': ' +
              organization.name, null, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
        }
      }, (error) => {
        this.notification.error(
            this.translate.instant('Organization could not be deleted') + ': ' +
            organization.name, error, this.COMPONENT_NAME, true);
        this.loadingIndicator.hide();
      });
    });


  }

  private copyOrganizationProperties(source: Organization, target: Organization | null): Organization {
    if (target == null) {
      target = new Organization();
    }

    target.id = source.id;
    target.name = source.name;
    target.description = source.description;
    target.agreement = source.agreement;
    target.userAgreementUrl = source.userAgreementUrl;
    target.privacyPolicyUrl = source.privacyPolicyUrl;
    if (source.parentTenant === null || source.parentTenant <= 0) {
      target.parentTenant = null;
    } else {
      target.parentTenant = source.parentTenant;
    }

    if (source.tableauConfiguration) {
      target.tableauConfiguration = source.tableauConfiguration;
    }

    target.subTenants = source.subTenants;
    if (source.parentTenantName) {
      target.parentTenantName = source.parentTenantName.trim();
    }

    return target;
  }

  private createOrganizationRemoveWarningBody(organization, machines, users, subTenants): string {
    let body: string = this.translate.instant(
        'The organization: %s cannot be removed since it has users or machines assigned or has organization descendants.');
    body = body.replaceAll('%s', organization.name.toBold()) + '<br>';

    if (subTenants && subTenants.length > 0) {
      body += '<br/>' + this.translate.instant('Organizations').toBold() +
          ':<div style="overflow-y: auto;max-height: 200px;">' +
          subTenants.map(function (elem) {
            return elem.name;
          }).join(', ') + '</div>';
    }

    if (machines && machines.length > 0) {
      body += '<br/>' + this.translate.instant('Machines').toBold() +
          ':<div style="overflow-y: auto;max-height: 200px;">' +
          machines.map(function (elem) {
            return elem.name;
          }).join(', ') + '</div>';
    }
    if (users && users.length > 0) {
      body += '<br/>' + this.translate.instant('Users').toBold() +
          ':<div style="overflow-y: auto;max-height: 200px;">' +
          users.map(function (elem) {
            return elem.username;
          }).join(', ') + '</div>';
    }
    return body;
  }
}
